import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/context/LanguageContext';
import { Facebook, Mail } from 'lucide-react';
import AccountInfoSection from './AccountInfoSection';
import UserInfoSection from './UserInfoSection';
import TermsCheckbox from './TermsCheckbox';

interface MultiStepSignUpProps {
  form: UseFormReturn<any>;
  onSubmit: (values: any) => void;
  isLoading: boolean;
  passwordRequirements: {
    length: boolean;
    uppercase: boolean;
    lowercase: boolean;
    number: boolean;
    special: boolean;
  };
  onUsernameBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  onEmailBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  onSponsorBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  sponsorValid: boolean;
}

const MultiStepSignUp = ({ 
  form, 
  onSubmit, 
  isLoading,
  passwordRequirements,
  onUsernameBlur,
  onEmailBlur,
  onSponsorBlur,
  sponsorValid
}: MultiStepSignUpProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const { t } = useLanguage();

  const handleNext = async () => {
    console.log('Validating Step 1 fields...');
    const step1Fields = ['sponsor', 'email', 'confirm_email', 'password', 'confirm_password', 'terms'];
    const isStep1Valid = await form.trigger(step1Fields);
    
    console.log('Step 1 validation result:', isStep1Valid);
    console.log('Step 1 form errors:', form.formState.errors);
    
    if (isStep1Valid) {
      console.log('Moving to Step 2');
      setCurrentStep(2);
    } else {
      console.log('Step 1 validation failed, staying on current step');
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  const handleSubmit = async (values: any) => {
    console.log('Step 2: Validating all fields before submission...');
    const step2Fields = ['username', 'first_name', 'last_name', 'date_of_birth', 'gender', 'country', 'country_code', 'phone'];
    const isStep2Valid = await form.trigger(step2Fields);
    
    console.log('Step 2 validation result:', isStep2Valid);
    console.log('All form values:', values);
    console.log('Form errors:', form.formState.errors);
    
    if (isStep2Valid) {
      console.log('All validations passed, submitting form...');
      onSubmit(values);
    } else {
      console.log('Step 2 validation failed, not submitting');
    }
  };

  if (currentStep === 1) {
    return (
      <div className="space-y-6">
        <div hidden className="space-y-3">
          <Button
            type="button"
            variant="outline"
            className="w-full bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white"
            disabled
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('auth.signUpWithGoogle')}
          </Button>
          
          <Button
            type="button"
            variant="outline"
            className="w-full bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white"
            disabled
          >
            <Facebook className="w-5 h-5 mr-2" />
            {t('auth.signUpWithFacebook')}
          </Button>
        </div>

        <div hidden className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-white/20" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-gray-900 px-2 text-neutral-400">{t('auth.orContinueWithEmail')}</span>
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-4">
              <AccountInfoSection 
                form={form} 
                passwordRequirements={passwordRequirements}
                onUsernameBlur={onUsernameBlur}
                onEmailBlur={onEmailBlur}
                onSponsorBlur={onSponsorBlur}
                sponsorValid={sponsorValid}
                showOnlyEmailAndPassword={true}
              />
            </div>
          </div>
          
          <div className={!sponsorValid ? 'opacity-50 pointer-events-none' : ''}>
            <TermsCheckbox form={form} />
          </div>
          
          <Button 
            type="button"
            onClick={handleNext}
            disabled={!sponsorValid}
            className="w-full bg-finance-800 hover:bg-finance-900"
          >
            {t('auth.next')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <Button
          type="button"
          variant="ghost"
          onClick={handleBack}
          className="text-neutral-300 hover:text-white hover:bg-white/10"
        >
          ← {t('auth.back')}
        </Button>
        <span className="text-sm text-neutral-400">{t('auth.stepTwoOfTwo')}</span>
      </div>

      <UserInfoSection form={form} onUsernameBlur={onUsernameBlur} />
      
      <Button 
        type="submit" 
        className="w-full bg-finance-800 hover:bg-finance-900" 
        disabled={isLoading}
      >
        {isLoading ? (
          <span className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {t('common.loading')}
          </span>
        ) : (
          t('auth.signUp')
        )}
      </Button>
    </form>
  );
};

export default MultiStepSignUp;
